import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

export interface PipelineFieldEvidenceItem {
  content_id: string;
  source_title?: string | null;
  source_type?: string | null;
  evidence_text?: string | null;
  page_or_slide?: string | null;
  published_date?: string | null;
  assertion_keys?: string[];
}

export interface PipelineEvidenceField {
  key: string;
  label: string;
  value?: string | string[] | null;
}

interface PipelineFieldEvidenceProps {
  fields: PipelineEvidenceField[];
  biologicalTargetStated: boolean;
  evidenceByField: Record<string, PipelineFieldEvidenceItem[]>;
}

const renderValue = (value?: string | string[] | null): string =>
  Array.isArray(value) ? value.join(', ') : value || '—';

/** Display source-derived pipeline values beside the passages that support them. */
export const PipelineFieldEvidence: React.FC<PipelineFieldEvidenceProps> = ({
  fields,
  biologicalTargetStated,
  evidenceByField,
}) => (
  <Stack spacing={1.5}>
    <Chip
      size="small"
      variant="outlined"
      color={biologicalTargetStated ? 'success' : 'default'}
      label={
        biologicalTargetStated
          ? 'Target stated in source'
          : 'Target not stated in source'
      }
      sx={{ alignSelf: 'flex-start' }}
    />
    {fields
      .filter((field) =>
        Array.isArray(field.value) ? field.value.length > 0 : Boolean(field.value),
      )
      .map((field) => (
        <Box key={field.key}>
          <Typography variant="caption" color="text.secondary">
            {field.label}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {renderValue(field.value)}
          </Typography>
          {(evidenceByField[field.key] || []).map((item, index) => (
            <Box key={`${field.key}-${item.content_id}-${index}`} sx={{ mt: 0.5, pl: 1.5 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                “{item.evidence_text}”
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.source_title || item.content_id}
                {item.page_or_slide ? ` · p/s ${item.page_or_slide}` : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
  </Stack>
);
